import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import type { RcFile } from "antd/es/upload";
import moment from "moment";

import { createUserDocumentScanFromDocument } from "services";

import { hashFile, useGetFarmDetails, useGetFarmReportingYear, useGetFarms, useTranslation } from "utils";

import { DOC_SCAN_MAX_FILE_SIZE_BYTES, DOC_SCAN_MAX_FILE_SIZE_MB } from "consts";

import { AlertBanner, Button, Col, Icon, Option, Row, Select, Typography, UploadFile, Message as message } from "components";

import { PdfValidationError, validatePdfContent } from "pages/app/document-scan/utils/validatePdfContent";

const BENEFITS_KEYS = ["benefit1", "benefit2", "benefit3"] as const;

// Content validation is a heuristic, so these outcomes only warn — the farmer can still submit the document.
const WARNING_KEYS = ["yearMismatch", "farmMismatch", "documentTypeMismatch"] as const;

type UploadWarningKey = (typeof WARNING_KEYS)[number];

const isWarningKey = (error?: PdfValidationError): error is UploadWarningKey => !!error && WARNING_KEYS.includes(error as UploadWarningKey);

type UploadStepProps = {
  onUploadSuccess: (docScanId: string) => void;
  serverError?: string;
};

export const UploadStep = ({ onUploadSuccess, serverError }: UploadStepProps) => {
  const { t } = useTranslation("docScans");

  const { currentFarm } = useGetFarms();

  const { data: reportingYearData } = useGetFarmReportingYear(currentFarm?.id || null);
  const { data: farmDetails } = useGetFarmDetails({ farmId: currentFarm?.id });

  const currentYear = reportingYearData?.activeFarmReportingYear ?? moment().year();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [uploadState, setUploadState] = useState<{
    success: boolean;
    // Technically the upload in this component will only happen on submit button click,
    // but the UploadFile component uses "uploading" as loading state, so we will misuse when validating
    uploading: boolean;
    filename?: string;
    errorMessage?: string;
    warningKey?: UploadWarningKey;
  }>({
    success: false,
    uploading: false,
    filename: undefined,
    errorMessage: serverError,
  });

  const yearOptions = [
    { label: `${currentYear}`, value: currentYear },
    { label: `${currentYear - 1}`, value: currentYear - 1 },
    { label: `${currentYear - 2}`, value: currentYear - 2 },
    { label: `${currentYear - 3}`, value: currentYear - 3 },
    { label: `${currentYear - 4}`, value: currentYear - 4 },
  ];

  const { mutate: createDocScan, isPending: isCreating } = useMutation({
    mutationFn: createUserDocumentScanFromDocument,
    onSuccess: (data) => {
      onUploadSuccess(data.id);
    },
    onError: () => {
      setUploadState((prev) => ({
        ...prev,
        uploading: false,
      }));
      message({
        content: t("upload.errors.uploadFailed"),
        icon: "gremlin",
      });
    },
  });

  const handleSubmit = () => {
    if (!selectedFile) {
      setUploadState((prev) => ({
        ...prev,
        success: false,
        errorMessage: t("upload.errors.noFile"),
      }));
      return;
    }

    setUploadState((prev) => ({ ...prev, uploading: true }));

    const reader = new FileReader();

    reader.onerror = () => {
      setUploadState((prev) => ({ ...prev, uploading: false }));
      message({
        content: t("upload.errors.readError"),
        icon: "gremlin",
      });
    };

    reader.onload = async () => {
      const { result } = reader;

      if (typeof result !== "string" || !result) {
        setUploadState((prev) => ({ ...prev, uploading: false }));
        message({
          content: t("upload.errors.processError"),
          icon: "gremlin",
        });
        return;
      }

      createDocScan({
        file: result,
        fileType: "application/pdf",
        fileName: selectedFile.name,
        fileHash: await hashFile(selectedFile),
        year: selectedYear,
        type: "match",
      });
    };

    reader.readAsDataURL(selectedFile);
  };

  const handleFileBeforeUpload = (file: RcFile) => {
    const isPdf = file.type === "application/pdf";
    const isValidSize = file.size <= DOC_SCAN_MAX_FILE_SIZE_BYTES;

    if (!isPdf) {
      setUploadState({
        success: false,
        uploading: false,
        filename: undefined,
        errorMessage: t("upload.errors.invalidType"),
      });
      setSelectedFile(null);
      return false;
    }

    if (!isValidSize) {
      setUploadState({
        success: false,
        uploading: false,
        filename: undefined,
        errorMessage: t("upload.errors.fileTooLarge", { maxSizeMb: DOC_SCAN_MAX_FILE_SIZE_MB }),
      });
      setSelectedFile(null);
      return false;
    }

    setUploadState({
      success: false,
      uploading: true,
      filename: file.name,
      errorMessage: undefined,
    });

    validatePdfContent(file, selectedYear, farmDetails ?? undefined).then(({ isValid, error }) => {
      const warningKey = isWarningKey(error) ? error : undefined;

      // Valid documents and the warn-only outcomes both stay selected, so the farmer can submit either way.
      if (isValid || warningKey) {
        setSelectedFile(file);
        setUploadState({
          success: true,
          uploading: false,
          filename: file.name,
          warningKey,
        });
        return;
      }

      setSelectedFile(null);
      setUploadState({
        success: false,
        uploading: false,
        filename: undefined,
        errorMessage: error === "countryNotSupported" ? t("upload.errors.countryNotSupported") : t("upload.errors.emptyPdf"),
      });
    });

    return false;
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    setUploadState({
      success: false,
      uploading: false,
      filename: undefined,
      errorMessage: undefined,
    });
  };

  const { warningKey, ...uploadFileState } = uploadState;

  return (
    <div className="doc-scan-content">
      <Row justify="center">
        <Col useDefaultSm={true}>
          <div className="doc-scan-icon-circle">
            <Icon iconName="tabler/file-check" size="l" color="white" />
          </div>
        </Col>
      </Row>

      <Row className="margin-top-100">
        <Col md={24}>
          <Typography variant="h1" className="center">
            {t("upload.heading")}
          </Typography>
        </Col>
      </Row>

      <Row>
        <Col md={24} className="center">
          <Typography variant="paragraph-m" className="center">
            {t("upload.subtitle")}
          </Typography>
        </Col>
      </Row>

      <Row className="margin-top-200 padding-bottom-200">
        <Col md={24}>
          <div className="doc-scan-year-selector">
            <Select label={t("upload.yearLabel")} value={selectedYear} onChange={(value) => setSelectedYear(value as number)}>
              {yearOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={24}>
          <UploadFile
            accept=".pdf"
            label={t("upload.dragDrop")}
            subtitle={t("upload.browseFiles")}
            helpMessage={t("upload.pdfOnly", { maxSizeMb: DOC_SCAN_MAX_FILE_SIZE_MB })}
            {...uploadFileState}
            isWarning={true}
            loadingMessage={t("upload.validatingFile")}
            warning={
              warningKey && (
                <AlertBanner
                  dataTestId="doc-scan-upload-warning"
                  className="margin-top-50"
                  type="warning"
                  title={t(`upload.warnings.${warningKey}.title`)}
                  headingSize="paragraph-m"
                  isTitleBold={true}
                  description={t("upload.warnings.description")}
                  descriptionSize="paragraph-m"
                  gap={4}
                />
              )
            }
            beforeUpload={handleFileBeforeUpload}
            onDelete={handleFileDelete}
          />
        </Col>
      </Row>

      <Row className="margin-top-200 padding-bottom-200">
        <Col md={24} className="center">
          <Button type="primary" dataTestId="doc-scan-submit" onClick={handleSubmit} loading={isCreating} disabled={!selectedFile || !selectedYear}>
            {t("upload.submit")}
          </Button>
        </Col>
      </Row>

      <Row>
        {/* prelint-ignore-col */}
        {BENEFITS_KEYS.map((key) => (
          <Col key={key} md={8}>
            <div className="doc-scan-benefit-item">
              <Icon iconName="tabler/circle-check" size="m" />
              <Typography variant="paragraph-m" color="grey-600">
                {t(`upload.benefits.${key}`)}
              </Typography>
            </div>
          </Col>
        ))}
      </Row>

      <Row className="margin-top-100">
        <Col md={24} className="center">
          <Typography variant="paragraph-m" color="grey-600">
            {t("upload.privacyNote")}
          </Typography>
          <br />
          <Typography variant="paragraph-m" color="grey-600">
            {t("upload.processingNote")}
          </Typography>
        </Col>
      </Row>
    </div>
  );
};
